import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminSortableTableHeader,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminSeverityText,
  AdminRowAvatar,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowAction,
  AdminAlertText,
  AdminRowMenuIcon,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminFormDropdownChevron,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import defaultUserAvatar from '../../../assets/img/User.png';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';

/**
 * Screen 1 columns — old layout positions, new spec fields:
 * Ticket ID · Submitted By · Submitted As · Ticket Type · Subject · Priority ·
 * Support Team Member · Status · Submitted On · Last Updated · Actions
 * (maps old Account→Submitted By, Type→Ticket Type, Updated→Last Updated; Submitted As added after Submitted By)
 */
/** Flexible fr columns (same pattern as Moderation / F&S) so the table fits without forced overflow. */
const SUPPORT_TICKETS_GRID =
  'minmax(0, 1.05fr) minmax(0, 1.25fr) minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 0.7fr) minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.05fr) minmax(0, 1.05fr) minmax(62px, auto)';

/** §5 — Support Ticket statuses (no Decided). */
const STATUS_OPTIONS = [
  'New',
  'In Progress',
  'Awaiting Reply',
  'Resolved',
  'Closed',
  'Withdrawn',
];

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];

const TICKET_TYPE_OPTIONS = [
  'General Enquiry',
  'Account Issue',
  'Billing & Subscriptions',
  'Technical Issue',
  'Feedback and Suggestions',
  'Other',
];

const SUPPORT_TEAM_MEMBERS = [
  'Bilal Ahmed',
  'Sana Malik',
  'Hamza Ali',
  'Jordan Lee',
  'Olivia Smith',
];

/** Demo: treat as manager so Assign / Reassign appear on the ⋮ menu (§6). */
const CAN_MANAGE_ASSIGN = true;
const CURRENT_ACTOR = 'Bilal Ahmed';

const formatAssignedDisplay = (raw) => {
  if (!raw || String(raw).toLowerCase() === 'unassigned') return '—';
  const name = String(raw)
    .replace(/^Fraud\s*&\s*Security — /i, '')
    .replace(/^(Admin|Compliance|Support|Moderator|Owner|Finance|Development) — /i, '')
    .replace(/^Admin: /, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .split(' (')[0]
    .trim();
  return name || '—';
};

const isLinkedTicket = (ticket) =>
  Boolean(
    ticket?.isComplianceAskMoreInfo ||
      ticket?.linkedCaseId ||
      ticket?.source === 'Compliance — Ask More Info'
  );

const normalizeListStatus = (status) => {
  if (status === 'Decided') return 'Resolved';
  if (status === 'Submitted') return 'New';
  return status || 'New';
};

const generateDummyTickets = () => {
  const baseRows = [
    {
      ticketId: 'ST-000185',
      submittedByName: 'Sana Malik',
      submittedAs: '—',
      email: 'compliance@apexplumbing.example.com',
      subject: 'Compliance information request — Apex Plumbing — Plumbing Licence',
      ticketType: 'General Enquiry',
      priority: 'Medium',
      assignee: 'Sana Malik',
      status: 'Awaiting Reply',
      source: 'Compliance — Ask More Info',
      businessId: 'BUS-001042',
      businessName: 'Apex Plumbing',
      relatedRecordType: 'Compliance Case',
      relatedRecordId: 'CMP-000218',
      linkedCaseModule: 'Compliance',
      linkedCaseId: 'CMP-000218',
      complianceResponsePeriodDays: 14,
      complianceDueDate: new Date('2026-08-22T10:00:00'),
      complianceResponseProtection: true,
      submittedOn: new Date('2026-08-08T10:00:00'),
      lastUpdated: new Date('2026-08-08T10:00:00'),
    },
    {
      ticketId: 'ST-000184',
      submittedByName: 'Aisha Khan',
      submittedAs: 'Customer',
      email: 'aisha.khan@email.com',
      subject: 'Verification code not received',
      ticketType: 'Account Issue',
      priority: 'Medium',
      assignee: 'Bilal Ahmed',
      status: 'In Progress',
      source: 'Customer Dashboard',
      customerId: 'CUS-001184',
      submittedOn: new Date('2026-08-08T14:22:00'),
      lastUpdated: new Date('2026-08-08T14:41:00'),
    },
    {
      ticketId: 'ST-000183',
      submittedByName: 'Westside Plumbing',
      submittedAs: 'Business',
      email: 'accounts@westsideplumbing.example.com',
      subject: 'Subscription invoice question',
      ticketType: 'Billing & Subscriptions',
      priority: 'Medium',
      assignee: 'Sana Malik',
      status: 'Resolved',
      source: 'Business Dashboard',
      businessId: 'BUS-002114',
      relatedRecordType: 'Invoice',
      relatedRecordId: 'INV-002461',
      submittedOn: new Date('2026-08-08T13:48:00'),
      lastUpdated: new Date('2026-08-08T15:10:00'),
    },
    {
      ticketId: 'ST-000182',
      submittedByName: 'Daniel Reed',
      submittedAs: 'Visitor',
      email: 'daniel.reed@email.com',
      subject: 'Dashboard page not loading',
      ticketType: 'Technical Issue',
      priority: 'High',
      assignee: null,
      status: 'New',
      source: 'Help Centre',
      submittedOn: new Date('2026-08-08T12:34:00'),
      lastUpdated: new Date('2026-08-08T12:34:00'),
    },
    {
      ticketId: 'ST-000181',
      submittedByName: 'Mia Wilson',
      submittedAs: 'Customer',
      email: 'mia.wilson@email.com',
      subject: 'Suggestion for location filters',
      ticketType: 'Feedback and Suggestions',
      priority: 'Low',
      assignee: 'Hamza Ali',
      status: 'Awaiting Reply',
      source: 'Customer Dashboard',
      customerId: 'CUS-001181',
      submittedOn: new Date('2026-08-07T16:05:00'),
      lastUpdated: new Date('2026-08-08T10:12:00'),
    },
    {
      ticketId: 'ST-000180',
      submittedByName: 'Rapid Electrical',
      submittedAs: 'Business',
      email: 'hello@rapidelectrical.example.com',
      subject: 'Updating business contact details',
      ticketType: 'General Enquiry',
      priority: 'Low',
      assignee: 'Bilal Ahmed',
      status: 'Withdrawn',
      source: 'Business Dashboard',
      businessId: 'BUS-002180',
      submittedOn: new Date('2026-07-30T11:20:00'),
      lastUpdated: new Date('2026-08-06T11:20:00'),
    },
    {
      ticketId: 'ST-000179',
      submittedByName: 'Olivia Smith',
      submittedAs: 'Customer',
      email: 'olivia.smith@email.com',
      subject: 'Payment receipt missing',
      ticketType: 'Billing & Subscriptions',
      priority: 'Medium',
      assignee: 'Olivia Smith',
      status: 'Closed',
      source: 'Customer Dashboard',
      customerId: 'CUS-001179',
      submittedOn: new Date('2026-07-28T09:15:00'),
      lastUpdated: new Date('2026-08-05T17:00:00'),
    },
  ];

  const assignees = [...SUPPORT_TEAM_MEMBERS, null];
  const subjects = [
    'Unable to reset password',
    'Invoice PDF will not download',
    'App freezes on profile save',
    'Request to update trade categories',
    'Question about subscription renewal',
    'Two-factor code delay',
    'Business listing not appearing',
    'Payment method update failed',
    'Notification settings not saving',
    'Help with post scheduling',
    'Account email change request',
    'Duplicate charge on card',
    'Mobile layout issue on quotes',
    'Feature request — saved filters',
    'Login from new device blocked',
    'Trade licence upload stuck',
    'Customer message not delivered',
    'Subscription pause enquiry',
    'Search results incomplete',
  ];
  const names = [
    { name: 'Noah Patel', as: 'Customer', email: 'noah.patel@email.com' },
    { name: 'Coastal Plumbing', as: 'Business', email: 'hello@coastalplumbing.example.com' },
    { name: 'Emma Clarke', as: 'Customer', email: 'emma.clarke@email.com' },
    { name: 'Bright Spark Electrical', as: 'Business', email: 'ops@brightspark.example.com' },
    { name: 'Liam Foster', as: 'Visitor', email: 'liam.foster@email.com' },
    { name: 'Jordan Lee', as: 'Customer', email: 'jordan.lee@email.com' },
    { name: 'Peak Roofing Co.', as: 'Business', email: 'admin@peakroofing.example.com' },
    { name: 'Sofia Nguyen', as: 'Customer', email: 'sofia.nguyen@email.com' },
    { name: 'Urban Tiling', as: 'Business', email: 'contact@urbantiling.example.com' },
    { name: 'Chris Martin', as: 'Visitor', email: 'chris.martin@email.com' },
    { name: 'Hannah Brooks', as: 'Customer', email: 'hannah.brooks@email.com' },
    { name: 'Valley HVAC', as: 'Business', email: 'support@valleyhvac.example.com' },
    { name: 'Owen Fraser', as: 'Customer', email: 'owen.fraser@email.com' },
    { name: 'Metro Locksmiths', as: 'Business', email: 'info@metrolocks.example.com' },
    { name: 'Priya Shah', as: 'Customer', email: 'priya.shah@email.com' },
    { name: 'Green Lawn Care', as: 'Business', email: 'hello@greenlawn.example.com' },
    { name: 'Ryan Cooper', as: 'Visitor', email: 'ryan.cooper@email.com' },
    { name: 'Alex Morgan', as: 'Customer', email: 'alex.morgan@email.com' },
    { name: 'Summit Builders', as: 'Business', email: 'office@summitbuilders.example.com' },
  ];

  for (let i = 0; i < subjects.length; i += 1) {
    const person = names[i % names.length];
    const submittedOn = new Date(2026, 6, 29 - i, 9 + (i % 8), (i * 7) % 60, 0);
    const lastUpdated = new Date(submittedOn.getTime() + (i % 5) * 3600000);
    baseRows.push({
      ticketId: `ST-${String(178 - i).padStart(6, '0')}`,
      submittedByName: person.name,
      submittedAs: person.as,
      email: person.email,
      subject: subjects[i],
      ticketType: TICKET_TYPE_OPTIONS[i % TICKET_TYPE_OPTIONS.length],
      priority: PRIORITY_OPTIONS[i % 3],
      assignee: assignees[i % assignees.length],
      status: STATUS_OPTIONS[i % STATUS_OPTIONS.length],
      source:
        person.as === 'Business'
          ? 'Business Dashboard'
          : person.as === 'Customer'
            ? 'Customer Dashboard'
            : 'Help Centre',
      customerId: person.as === 'Customer' ? `CUS-${1000 + i}` : undefined,
      businessId: person.as === 'Business' ? `BUS-${2000 + i}` : undefined,
      submittedOn,
      lastUpdated,
    });
  }

  return baseRows.map((ticket, idx) => {
    const isComplianceAskMoreInfo = ticket.source === 'Compliance — Ask More Info';
    const customerName = ticket.submittedAs === 'Customer' ? ticket.submittedByName : null;
    const businessName =
      ticket.businessName ||
      (ticket.submittedAs === 'Business' ? ticket.submittedByName : null);
    const customerId =
      ticket.submittedAs === 'Customer'
        ? ticket.customerId || `CUS-${1100 + idx}`
        : null;
    const businessId =
      ticket.businessId ||
      (ticket.submittedAs === 'Business' || isComplianceAskMoreInfo
        ? ticket.businessId || `BUS-${2100 + idx}`
        : null);
    // Customer / Business (and Compliance recipient Business) get profile images; Visitors use placeholder.
    const accountAvatar =
      ticket.submittedAs === 'Customer'
        ? `https://i.pravatar.cc/150?img=${(idx % 70) + 1}`
        : ticket.submittedAs === 'Business' || isComplianceAskMoreInfo
          ? `https://i.pravatar.cc/150?img=${(idx % 70) + 20}`
          : null;
    return {
      ...ticket,
      status: normalizeListStatus(ticket.status),
      accountType: ticket.submittedAs,
      category: ticket.ticketType,
      channel: ticket.source,
      createdDate: ticket.submittedOn,
      customerName,
      businessName,
      accountId: customerId || businessId || null,
      customerId,
      businessId,
      accountAvatar,
      createdBy: ticket.submittedByName,
      type: isComplianceAskMoreInfo ? 'Business' : ticket.submittedAs,
      isComplianceAskMoreInfo,
      linkedCaseId: ticket.linkedCaseId || (isComplianceAskMoreInfo ? ticket.relatedRecordId : null),
    };
  });
};

const SupportTickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const gridTemplateColumns = SUPPORT_TICKETS_GRID;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    priority: null,
    supportTeamMember: null,
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [supportTeamOpen, setSupportTeamOpen] = useState(false);
  const statusRef = useRef(null);
  const priorityRef = useRef(null);
  const supportTeamRef = useRef(null);

  const [ticketRowActionsOpen, setTicketRowActionsOpen] = useState({});
  const ticketRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: ticketRowActionsMenuLayout,
    menuRef: ticketRowActionsMenuRef,
    placementReady: ticketRowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    ticketRowActionsOpen,
    ticketRowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.DOUBLE
  );

  const [allTicketItems, setAllTicketItems] = useState(() => generateDummyTickets());
  const [assignPopup, setAssignPopup] = useState(null);
  const [assignMember, setAssignMember] = useState('');
  const [assignReason, setAssignReason] = useState('');

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('submittedOn');
  const [sortOrder, setSortOrder] = useState('desc');

  const statusFilterOptions = useMemo(
    () => [{ value: null, label: 'All' }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: s }))],
    []
  );
  const priorityFilterOptions = useMemo(
    () => [{ value: null, label: 'All' }, ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))],
    []
  );
  const supportTeamFilterOptions = useMemo(() => {
    const names = new Set();
    allTicketItems.forEach((t) => {
      const label = formatAssignedDisplay(t.assignee);
      if (label !== '—') names.add(label);
    });
    return [
      { value: null, label: 'All' },
      ...[...names].sort().map((name) => ({ value: name, label: name })),
      { value: '__unassigned__', label: 'Unassigned' },
    ];
  }, [allTicketItems]);

  const toggleTicketRowActions = (rowKey) => {
    setTicketRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(ticketRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setTicketRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(priorityRef, () => setPriorityOpen(false));
  useClickOutside(supportTeamRef, () => setSupportTeamOpen(false));

  const matchesSearchQuery = (ticket, searchTerm) => {
    if (!searchTerm || searchTerm.trim().length === 0) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      ticket.ticketId?.toLowerCase().includes(term) ||
      ticket.subject?.toLowerCase().includes(term) ||
      ticket.submittedByName?.toLowerCase().includes(term) ||
      ticket.customerName?.toLowerCase().includes(term) ||
      ticket.businessName?.toLowerCase().includes(term) ||
      String(ticket.email || '')
        .toLowerCase()
        .includes(term) ||
      String(ticket.linkedCaseId || '')
        .toLowerCase()
        .includes(term) ||
      String(ticket.relatedRecordId || '')
        .toLowerCase()
        .includes(term)
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      setError(null);
      try {
        await new Promise((resolve) => setTimeout(resolve, 200));
        let filteredTickets = allTicketItems.filter((ticket) => {
          const matchesSearch = matchesSearchQuery(ticket, debouncedSearch);
          const matchesStatus =
            filters.status === null || normalizeListStatus(ticket.status) === filters.status;
          const matchesPriority =
            filters.priority === null || ticket.priority === filters.priority;
          const assigned = formatAssignedDisplay(ticket.assignee);
          let matchesTeam = true;
          if (filters.supportTeamMember === '__unassigned__') {
            matchesTeam = assigned === '—';
          } else if (filters.supportTeamMember !== null) {
            matchesTeam = assigned === filters.supportTeamMember;
          }
          return matchesSearch && matchesStatus && matchesPriority && matchesTeam;
        });

        filteredTickets.sort((a, b) => {
          let aValue = a[sortField];
          let bValue = b[sortField];

          if (sortField === 'submittedOn' || sortField === 'lastUpdated') {
            aValue = new Date(a[sortField]).getTime();
            bValue = new Date(b[sortField]).getTime();
          } else if (sortField === 'assignee') {
            aValue = formatAssignedDisplay(a.assignee);
            bValue = formatAssignedDisplay(b.assignee);
          } else if (sortField === 'status') {
            aValue = normalizeListStatus(a.status);
            bValue = normalizeListStatus(b.status);
          }

          if (aValue === null || aValue === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return sortOrder === 'asc' ? -1 : 1;

          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          }
          return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });

        const totalCount = filteredTickets.length;
        const computedTotalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, computedTotalPages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(totalCount);
        setTotalPages(computedTotalPages);
        setTickets(filteredTickets.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));
        if (clampedPage !== currentPage) setCurrentPage(clampedPage);
      } catch (err) {
        setError(err.message || 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [allTicketItems, debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'submittedOn' || field === 'lastUpdated' ? 'desc' : 'asc');
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    const datePart = d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  };

  /** Opening is view-only — does not assign or change status (§6). */
  const openTicket = (ticket) => {
    navigate(`/admin/support/tickets/${ticket.ticketId}`, {
      state: { from: location.pathname, ticket },
    });
  };

  const canShowAssignAction = (ticket) => {
    if (!CAN_MANAGE_ASSIGN || isLinkedTicket(ticket)) return false;
    const status = normalizeListStatus(ticket.status);
    return status === 'New' || status === 'In Progress' || status === 'Awaiting Reply' || status === 'Resolved';
  };

  const openAssignPopup = (ticket, mode) => {
    setTicketRowActionsOpen({});
    setAssignPopup({ ticket, mode });
    setAssignMember(SUPPORT_TEAM_MEMBERS[0] || CURRENT_ACTOR);
    setAssignReason('');
  };

  const submitAssignPopup = () => {
    if (!assignPopup?.ticket || !assignMember.trim() || !assignReason.trim()) return;
    const { ticket, mode } = assignPopup;
    const now = new Date();
    setAllTicketItems((prev) =>
      prev.map((row) => {
        if (row.ticketId !== ticket.ticketId) return row;
        return {
          ...row,
          assignee: assignMember.trim(),
          status: mode === 'assign' ? 'In Progress' : normalizeListStatus(row.status),
          lastUpdated: now,
        };
      })
    );
    setAssignPopup(null);
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Support Tickets</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Ticket ID, Subject, Submitted By, Email, Linked Case ID..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Search support tickets"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={statusFilterOptions}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Priority"
                value={filters.priority}
                options={priorityFilterOptions}
                onChange={(value) => handleFilterChange('priority', value)}
                menuRef={priorityRef}
                open={priorityOpen}
                setOpen={setPriorityOpen}
              />
              <AdminListFilterDropdown
                label="Support Team Member"
                value={filters.supportTeamMember}
                options={supportTeamFilterOptions}
                onChange={(value) => handleFilterChange('supportTeamMember', value)}
                menuRef={supportTeamRef}
                open={supportTeamOpen}
                setOpen={setSupportTeamOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="mb-4">{error}</AdminAlertText>
              <AdminRowAction size="sm" onClick={() => window.location.reload()}>
                Retry
              </AdminRowAction>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No tickets match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div className="min-w-0">
                <div
                  className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                  style={{ gridTemplateColumns }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('ticketId')}
                    >
                      Ticket ID
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('submittedByName')}
                    >
                      Submitted By
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('submittedAs')}
                    >
                      Submitted As
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('ticketType')}
                    >
                      Ticket Type
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('subject')}
                    >
                      Subject
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('priority')}
                    >
                      Priority
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('assignee')}
                    >
                      Support Team Member
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('submittedOn')}
                    >
                      Submitted On
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminSortableTableHeader
                      className="text-left"
                      onClick={() => handleSort('lastUpdated')}
                    >
                      Last Updated
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center justify-center">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {tickets.map((ticket, index) => {
                  const status = normalizeListStatus(ticket.status);
                  const assignedLabel = formatAssignedDisplay(ticket.assignee);
                  const submittedBy = ticket.submittedByName || '—';
                  const rowKey = ticket.ticketId || `idx-${index}`;
                  const showAssign = canShowAssignAction(ticket);
                  const assignMode = status === 'New' ? 'assign' : 'reassign';
                  return (
                    <div
                      key={ticket.ticketId || index}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer"
                      style={{ gridTemplateColumns }}
                      onClick={() => openTicket(ticket)}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={ticket.ticketId}>
                          {ticket.ticketId || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 gap-2">
                        <AdminRowAvatar
                          src={ticket.accountAvatar || defaultUserAvatar}
                          alt={submittedBy}
                          onError={(e) => {
                            e.target.src = defaultUserAvatar;
                          }}
                        />
                        <AdminTableBody
                          className="truncate text-left min-w-0"
                          title={submittedBy}
                        >
                          {submittedBy}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate">
                          {ticket.submittedAs || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody
                          className="text-left w-full truncate"
                          title={ticket.ticketType}
                        >
                          {ticket.ticketType || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody
                          className="truncate text-left w-full"
                          title={ticket.subject || '—'}
                        >
                          {ticket.subject || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center">
                        <AdminSeverityText severity={ticket.priority} className="text-left w-full">
                          {ticket.priority || '—'}
                        </AdminSeverityText>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="text-left w-full truncate" title={assignedLabel}>
                          {assignedLabel}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge
                          status={status}
                          label={status}
                          className="whitespace-nowrap"
                        />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta
                          className="text-left whitespace-nowrap"
                          title={formatDateTime(ticket.submittedOn)}
                        >
                          {formatDateTime(ticket.submittedOn)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta
                          className="text-left whitespace-nowrap"
                          title={formatDateTime(ticket.lastUpdated)}
                        >
                          {formatDateTime(ticket.lastUpdated)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            ticketRowActionsRefs.current[rowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTicketRowActions(rowKey);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {ticketRowActionsOpen[rowKey] && ticketRowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={ticketRowActionsMenuLayout}
                              menuRef={ticketRowActionsMenuRef}
                              placementReady={ticketRowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTicket(ticket);
                                  setTicketRowActionsOpen({});
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                              {showAssign ? (
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAssignPopup(ticket, assignMode);
                                  }}
                                >
                                  {assignMode === 'assign' ? 'Assign' : 'Reassign'}
                                </AdminRowMenuItem>
                              ) : null}
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {assignPopup && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="dashboard-panel font-inter fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
              onClick={() => setAssignPopup(null)}
              role="presentation"
            >
              <div
                className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <AdminPageTitle className="!text-lg text-center">
                  {assignPopup.mode === 'reassign' ? 'Reassign ticket' : 'Assign ticket'}
                </AdminPageTitle>
                <div className="mt-6 space-y-4">
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Team member</AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        className={adminFormDetailSelectClasses()}
                        value={assignMember}
                        onChange={(e) => setAssignMember(e.target.value)}
                      >
                        {SUPPORT_TEAM_MEMBERS.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Reason</AdminDetailFieldLabel>
                    <textarea
                      className={`${adminFormDetailTextareaClasses()} min-h-[88px]`}
                      value={assignReason}
                      onChange={(e) => setAssignReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <AdminFormSecondaryAction
                    type="button"
                    size="lg"
                    onClick={() => setAssignPopup(null)}
                  >
                    Cancel
                  </AdminFormSecondaryAction>
                  <AdminHeaderAction
                    type="button"
                    size="lg"
                    disabled={!assignMember.trim() || !assignReason.trim()}
                    onClick={submitAssignPopup}
                  >
                    Save
                  </AdminHeaderAction>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </AdminLayout>
  );
};

export default SupportTickets;
